import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { GtoConnectLockup } from '../brand/GtoConnectLockup';
import { Yarlyk } from '../brand/Yarlyk';
import { BrandFrame } from '../brand/BrandFrame';
import { Icon } from '../components/Icon';
import { AudioMeter } from '../components/AudioMeter';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { api, ApiError, type MeetingView } from '../lib/api';
import {
  AUDIO_MODE_LABEL,
  AUDIO_MODE_NOTE,
  type AudioMode,
} from '../lib/audio';
import {
  CODEC_LABEL,
  CODEC_NOTE,
  detectSupportedCodecs,
  pickInitialCodec,
  type CodecPreference,
} from '../lib/codec';
import { VIDEO_MODE_LABEL, VIDEO_MODE_NOTE, type VideoMode } from '../lib/video';
import { participantsWord } from '../lib/format';
import { loadPrefs, savePrefs } from '../lib/storage';
import { useDevices, deviceLabel } from '../lib/useDevices';
import { useLocalPreview } from '../lib/usePreview';
import { WaitingScreen } from './WaitingScreen';
import type { JoinIntent } from '../App';
import './PrejoinScreen.css';

export function PrejoinScreen({
  code,
  onJoin,
  onBack,
}: {
  code: string;
  onJoin: (intent: JoinIntent) => void;
  onBack: () => void;
}) {
  const [meeting, setMeeting] = useState<MeetingView | null>(null);
  const [lookupError, setLookupError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    api
      .getMeeting(code)
      .then((data) => alive && setMeeting(data.meeting))
      .catch((err) => alive && setLookupError(err instanceof ApiError ? err : null));
    return () => {
      alive = false;
    };
  }, [code, reloadKey]);

  if (lookupError) {
    return <MeetingGone code={code} message={lookupError.message} onHome={onBack} />;
  }

  if (!meeting) {
    return (
      <div className="prejoin prejoin--loading">
        <span className="spinner" />
      </div>
    );
  }

  if (meeting.state === 'ended') {
    return (
      <MeetingGone
        code={code}
        message="Эта встреча уже завершена. Попросите организатора создать новую."
        onHome={onBack}
      />
    );
  }

  if (meeting.state === 'waiting') {
    return (
      <WaitingScreen meeting={meeting} onOpen={() => setReloadKey((n) => n + 1)} />
    );
  }

  return <PrejoinForm meeting={meeting} onJoin={onJoin} onBack={onBack} />;
}

// ── Встречи нет или она закончилась ───────────────────────────────────

function MeetingGone({
  code,
  message,
  onHome,
}: {
  code: string;
  message: string;
  onHome: () => void;
}) {
  return (
    <div className="gone">
      <div className="grid-bg" />
      <div className="gone__box">
        <BrandFrame inset={-16} />
        <Yarlyk side="bottom" tone="danger">
          Ссылка недействительна
        </Yarlyk>
        <h1 className="gone__title">ВСТРЕЧА НЕДОСТУПНА</h1>
        <p className="gone__text">{message}</p>
        <p className="gone__code">{code}</p>
        <button type="button" className="btn btn--primary btn--lg" onClick={onHome}>
          <Icon name="arrowRight" />
          На главную
        </button>
      </div>
      <footer className="gone__footer">
        <GtoConnectLockup markHeight={20} />
      </footer>
    </div>
  );
}

// ── Подготовка к входу ────────────────────────────────────────────────

function PrejoinForm({
  meeting,
  onJoin,
  onBack,
}: {
  meeting: MeetingView;
  onJoin: (intent: JoinIntent) => void;
  onBack: () => void;
}) {
  const code = meeting.code;
  const [prefs, setPrefs] = useState(loadPrefs);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const supportedCodecs = useMemo(detectSupportedCodecs, []);
  const autoStart = useMemo(() => pickInitialCodec(supportedCodecs), [supportedCodecs]);

  const devices = useDevices();
  const preview = useLocalPreview({
    cameraId: prefs.cameraId,
    micId: prefs.micId,
    cameraEnabled: prefs.cameraEnabled,
    micEnabled: prefs.micEnabled,
  });

  function update(patch: Partial<typeof prefs>) {
    setPrefs((current) => ({ ...current, ...patch }));
    savePrefs(patch);
  }

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.srcObject = preview.stream;
  }, [preview.stream]);

  useEffect(() => {
    if (preview.permission === 'granted') devices.refresh();
  }, [preview.permission]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!prefs.name) nameRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleJoin(event: FormEvent) {
    event.preventDefault();

    const name = prefs.name.trim();
    if (!name) {
      setError('Представьтесь, чтобы участники понимали, кто вошёл');
      nameRef.current?.focus();
      return;
    }

    setError('');
    setJoining(true);

    try {
      const credentials = await api.getToken(code, name);
      onJoin({
        credentials,
        displayName: name,
        cameraEnabled: prefs.cameraEnabled,
        micEnabled: prefs.micEnabled,
        cameraId: prefs.cameraId,
        micId: prefs.micId,
        speakerId: prefs.speakerId,
        codec: prefs.codec,
        videoMode: prefs.videoMode,
        audioMode: prefs.audioMode,
        noiseSuppression: prefs.noiseSuppression,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось войти во встречу');
      setJoining(false);
    }
  }

  const blocked = preview.permission === 'denied' || preview.permission === 'missing';
  const showVideo = prefs.cameraEnabled && preview.stream && !blocked;

  return (
    <div className="prejoin">
      <div className="grid-bg" />

      <header className="prejoin__header">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
          <Icon name="arrowRight" size={16} className="prejoin__back-icon" />
          На главную
        </button>
        <GtoConnectLockup markHeight={20} />
      </header>

      <main className="prejoin__main">
        <section className="prejoin__stage" aria-label="Проверка камеры и микрофона">
          <BrandFrame inset={-12} />

          <div className="prejoin__preview">
            <video
              ref={videoRef}
              className={`prejoin__video ${showVideo ? '' : 'prejoin__video--hidden'}`}
              autoPlay
              playsInline
              muted
            />

            {!showVideo && (
              <div className="prejoin__placeholder">
                <Icon name={blocked ? 'alert' : 'camOff'} size={30} />
                <p>
                  {blocked
                    ? preview.error
                    : prefs.cameraEnabled
                      ? 'Включаем камеру…'
                      : 'Камера выключена'}
                </p>
              </div>
            )}

            <div className="prejoin__preview-top">
              <Yarlyk side="top" tone="ghost">
                <span className={`dot ${meeting.participants ? 'dot--live' : ''}`} aria-hidden />
                {meeting.participants
                  ? `${meeting.participants} ${participantsWord(meeting.participants)} на встрече`
                  : 'Вы войдёте первым'}
              </Yarlyk>
            </div>

            <div className="prejoin__preview-controls">
              <button
                type="button"
                className={`prejoin__toggle ${prefs.micEnabled ? '' : 'prejoin__toggle--off'}`}
                onClick={() => update({ micEnabled: !prefs.micEnabled })}
                aria-pressed={prefs.micEnabled}
                title={prefs.micEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
              >
                <Icon name={prefs.micEnabled ? 'mic' : 'micOff'} />
              </button>

              <AudioMeter stream={preview.stream} active={prefs.micEnabled && !blocked} />

              <button
                type="button"
                className={`prejoin__toggle ${prefs.cameraEnabled ? '' : 'prejoin__toggle--off'}`}
                onClick={() => update({ cameraEnabled: !prefs.cameraEnabled })}
                aria-pressed={prefs.cameraEnabled}
                title={prefs.cameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
              >
                <Icon name={prefs.cameraEnabled ? 'cam' : 'camOff'} />
              </button>
            </div>
          </div>
        </section>

        <section className="prejoin__side">
          <div className="prejoin__room">
            <span className="eyebrow">{meeting.scheduled ? 'Встреча по расписанию' : 'Комната'}</span>
            <h1 className="prejoin__title">{meeting.title}</h1>
          </div>

          <form className="prejoin__form" onSubmit={handleJoin} noValidate>
            <label className="field">
              <span className="field__label">Как вас представить</span>
              <input
                ref={nameRef}
                className="input"
                value={prefs.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="Имя и фамилия"
                maxLength={60}
                autoComplete="name"
                aria-invalid={Boolean(error)}
              />
            </label>

            {error && (
              <p className="prejoin__error" role="alert">
                <Icon name="alert" size={16} />
                {error}
              </p>
            )}

            {/*
              Вход и ссылка стоят сразу за именем — то есть в самом верху,
              до всех настроек. Листать до них не нужно, а настройки ниже
              остаются для тех, кому они действительно понадобились.
            */}
            <div className="prejoin__actions">
              <button
                type="submit"
                className="btn btn--go btn--lg"
                disabled={joining || !prefs.name.trim()}
              >
                {joining ? <span className="spinner" /> : <Icon name="arrowRight" />}
                {joining ? 'Подключаемся' : 'Войти во встречу'}
              </button>
              <CopyLinkButton code={code} />
            </div>

            <label className="field">
              <span className="field__label">Камера</span>
              <select
                className="select"
                value={prefs.cameraId}
                onChange={(e) => update({ cameraId: e.target.value })}
                disabled={devices.cameras.length === 0}
              >
                <option value="">По умолчанию</option>
                {devices.cameras.map((device, i) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {deviceLabel(device, i, 'Камера')}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Микрофон</span>
              <select
                className="select"
                value={prefs.micId}
                onChange={(e) => update({ micId: e.target.value })}
                disabled={devices.mics.length === 0}
              >
                <option value="">По умолчанию</option>
                {devices.mics.map((device, i) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {deviceLabel(device, i, 'Микрофон')}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="prejoin__more"
              onClick={() => setShowSettings((open) => !open)}
              aria-expanded={showSettings}
            >
              <Icon name="settings" size={16} />
              Качество звука и видео
              <Icon
                name="arrowRight"
                size={14}
                className={`prejoin__more-chevron ${showSettings ? 'prejoin__more-chevron--open' : ''}`}
              />
            </button>

            {showSettings && (
              <div className="prejoin__settings">
                <label className="field">
                  <span className="field__label">Качество видео</span>
                  <select
                    className="select"
                    value={prefs.videoMode}
                    onChange={(e) => update({ videoMode: e.target.value as VideoMode })}
                  >
                    {(['auto', 'high', 'saver'] as VideoMode[]).map((mode) => (
                      <option key={mode} value={mode}>
                        {VIDEO_MODE_LABEL[mode]}
                      </option>
                    ))}
                  </select>
                  <span className="prejoin__hint">{VIDEO_MODE_NOTE[prefs.videoMode]}</span>
                </label>

                <label className="field">
                  <span className="field__label">Звук</span>
                  <select
                    className="select"
                    value={prefs.audioMode}
                    onChange={(e) => update({ audioMode: e.target.value as AudioMode })}
                  >
                    {(['auto', 'speech'] as AudioMode[]).map((mode) => (
                      <option key={mode} value={mode}>
                        {AUDIO_MODE_LABEL[mode]}
                      </option>
                    ))}
                  </select>
                  <span className="prejoin__hint">{AUDIO_MODE_NOTE[prefs.audioMode]}</span>
                </label>

                <label className="switch">
                  <input
                    type="checkbox"
                    checked={prefs.noiseSuppression}
                    onChange={(e) => update({ noiseSuppression: e.target.checked })}
                  />
                  <span className="switch__track" aria-hidden>
                    <span className="switch__knob" />
                  </span>
                  <span className="switch__text">
                    <span className="switch__title">Шумоподавление</span>
                    <span className="switch__note">
                      Штатный фильтр браузера: убирает клавиатуру, вентилятор, улицу
                    </span>
                  </span>
                </label>

                {devices.speakers.length > 0 && (
                  <label className="field">
                    <span className="field__label">Динамики</span>
                    <select
                      className="select"
                      value={prefs.speakerId}
                      onChange={(e) => update({ speakerId: e.target.value })}
                    >
                      <option value="">По умолчанию</option>
                      {devices.speakers.map((device, i) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {deviceLabel(device, i, 'Динамики')}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="field">
                  <span className="field__label">Видеокодек</span>
                  <select
                    className="select"
                    value={prefs.codec}
                    onChange={(e) => update({ codec: e.target.value as CodecPreference })}
                  >
                    <option value="auto">Автоматически</option>
                    {supportedCodecs.map((codec) => (
                      <option key={codec} value={codec}>
                        {CODEC_LABEL[codec]}
                      </option>
                    ))}
                  </select>
                  <span className="prejoin__hint">
                    {prefs.codec === 'auto'
                      ? `Возьмём ${CODEC_LABEL[autoStart]}. Менять стоит, только если видео не идёт — кодек фиксируется на всю встречу`
                      : `${CODEC_NOTE[prefs.codec]}. Останется таким до конца встречи`}
                  </span>
                </label>
              </div>
            )}

          </form>
        </section>
      </main>
    </div>
  );
}
